import { useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Activity,
  Bell,
  BookA,
  BuildingComplex,
  ChartColumn,
  ChartLine,
  CircleQuestionMark,
  FileText,
  Globe,
  House,
  type LucideIcon,
  Megaphone,
  MessageSquareWarning,
  Scale,
  ScrollText,
  Settings,
  Shield,
  Target,
  ThumbsUp,
  Trophy,
  UserCog,
  Users,
} from 'lucide-react'

import styles from './styles.module.scss'

import Tooltip from '@/components/Tooltip'
import { useFlowStateGenerationEnabled } from '@/hooks/useFlowStateGenerationEnabled'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import { useSharedDictionaryApprovalEnabled } from '@/hooks/useSharedDictionaryApprovalEnabled'
import { getImpersonation, subscribeImpersonation } from '@/lib/impersonation'
import { DICTIONARY_SNIPPETS_BASE_PATH } from '@/pages/app/DictionarySnippets/routes'
import { selectEmail, selectIsAdmin } from '@/store/features/user/userSlice'
import { getIsInternalUser } from '@/utils'

interface SidebarItemProps {
  title: string
  link: string
  isActive: boolean
  icon?: LucideIcon
  className?: string
  onNavigate?: () => void
}

const SidebarItem = ({ title, link, isActive, icon: Icon, className, onNavigate }: SidebarItemProps) => {
  const target = link.startsWith('/') ? '_self' : '_blank'
  return (
    <NavLink to={link} className={`${styles.sidebarItem} ${className || ''}`} target={target} onClick={onNavigate}>
      <div className={`${styles.item} ${isActive ? styles.active : ''}`}>
        {Icon && <Icon size={16} />}
        {title}
      </div>
    </NavLink>
  )
}

const AUDIT_LOGS_ITEM = { title: 'Audit Logs', link: '/app/audit-logs', adminOnly: true, icon: ScrollText }

// Flag-gated by identity below, matching FLOW_STATE_ITEM, rather than by a field on the item.
const DICTIONARY_SNIPPETS_ITEM = {
  title: 'Dictionary & Snippets',
  link: DICTIONARY_SNIPPETS_BASE_PATH,
  adminOnly: true,
  icon: BookA,
}

const consoleItems: {
  title: string
  link: string
  adminOnly?: boolean
  icon?: LucideIcon
}[] = [
  { title: 'Home', link: '/app/home', icon: House },
  { title: 'Usage', link: '/app/usage', adminOnly: true, icon: ChartColumn },
  { title: 'Team', link: '/app/team', icon: Users },
  DICTIONARY_SNIPPETS_ITEM,
  AUDIT_LOGS_ITEM,
  { title: 'Settings', link: '/app/settings', icon: Settings },
  { title: 'Privacy', link: '/app/privacy', icon: Shield },
]

const FLOW_STATE_ITEM = {
  title: 'Flow State',
  link: '/app/flow-state',
  internal: true,
  icon: Activity,
}
const REPORTED_MEETINGS_ITEM = {
  title: 'Reported meetings',
  link: '/app/reported-meetings',
  internal: true,
  icon: MessageSquareWarning,
}
const ATTRIBUTION_VIEWER_ITEM = {
  title: 'Speaker attribution',
  link: '/app/attribution',
  internal: true,
  icon: Target,
}

const ANNOTATION_LEADERBOARD_ITEM = {
  title: 'Annotation leaderboard',
  link: '/app/annotation',
  internal: true,
  icon: Trophy,
}

const TURN_PREFERENCE_RESULTS_ITEM = {
  title: 'In-house vs. Soniox votes',
  link: '/app/attribution/turn-preference/results',
  internal: true,
  icon: Scale,
}

const SUMMARY_FEEDBACK_ITEM = {
  title: 'Summary feedback',
  link: '/app/summary-feedback',
  internal: true,
  icon: ThumbsUp,
}

const PREREAD_FEEDBACK_ITEM = {
  title: 'Preread feedback',
  link: '/app/preread-feedback',
  internal: true,
  icon: FileText,
}

const internalItems: {
  title: string
  link: string
  internal: boolean
  icon: LucideIcon
}[] = [
  {
    title: 'Notifications',
    link: '/app/notifications',
    internal: true,
    icon: Bell,
  },
  { title: 'Users', link: '/app/users', internal: true, icon: UserCog },
  {
    title: 'Enterprises & Billing',
    link: '/app/enterprises',
    internal: true,
    icon: BuildingComplex,
  },
  {
    title: 'Domain Management',
    link: '/app/domain-management',
    internal: true,
    icon: Globe,
  },
  {
    title: 'Marketing Campaigns',
    link: '/app/campaigns',
    internal: true,
    icon: Megaphone,
  },
  {
    title: 'Analytics Portal',
    link: '/app/analytics-portal',
    internal: true,
    icon: ChartLine,
  },
  FLOW_STATE_ITEM,
  REPORTED_MEETINGS_ITEM,
  ATTRIBUTION_VIEWER_ITEM,
  ANNOTATION_LEADERBOARD_ITEM,
  TURN_PREFERENCE_RESULTS_ITEM,
  SUMMARY_FEEDBACK_ITEM,
  PREREAD_FEEDBACK_ITEM,
]

const HelpButton = () => (
  <SidebarItem
    title="Help"
    link="mailto:support@wisprflow.ai?subject=Request%20for%20Help"
    isActive={false}
    icon={CircleQuestionMark}
    className={styles.helpButton}
  />
)

interface SidebarProps {
  id?: string
  className?: string
  hidden?: boolean
  onNavigate?: () => void
}

const Sidebar = ({ id, className, hidden, onNavigate }: SidebarProps) => {
  const location = useLocation()
  const email = useSelector(selectEmail)
  const isAdmin = useSelector(selectIsAdmin)
  const impersonating = useSyncExternalStore(subscribeImpersonation, getImpersonation)
  const flowStateEnabled = useFlowStateGenerationEnabled()
  const dictionarySnippetsEnabled = useSharedDictionaryApprovalEnabled()
  const isWisprInternal = getIsInternalUser(email) && !impersonating
  const isInternalAdmin = isWisprInternal && isAdmin
  const hasDepts = useHasDepartments()
  // Audit Logs itself isn't flag-gated here — it was reachable by any enterprise admin before
  // this PR added the Events tab's `audit-logs-ui` rollout flag, which AuditLogsPage applies to
  // that tab alone so existing SIEM-configuration users keep sidebar access.
  const visibleConsoleItems = consoleItems
    .filter((item) => !item.adminOnly || isAdmin)
    // Hidden while the flag is still resolving — the sidebar has no flicker cost, unlike the route.
    .filter((item) => item !== DICTIONARY_SNIPPETS_ITEM || (dictionarySnippetsEnabled ?? false))
    .map((item) => (item.link === '/app/team' && hasDepts ? { ...item, title: 'Organization' } : item))
  // Any Wispr account may annotate ground truth and read the annotation tallies; the rest of the Internal section is admin-only.
  const visibleInternalItems = internalItems.filter((item) =>
    item === ATTRIBUTION_VIEWER_ITEM || item === ANNOTATION_LEADERBOARD_ITEM || item === TURN_PREFERENCE_RESULTS_ITEM
      ? isWisprInternal
      : isInternalAdmin && (item !== FLOW_STATE_ITEM || flowStateEnabled)
  )
  // The results page lives under /app/attribution, so a prefix match would light up both entries.
  const isInternalItemActive = (item: { link: string }) =>
    item === ATTRIBUTION_VIEWER_ITEM
      ? location.pathname.startsWith(item.link) && !location.pathname.startsWith(TURN_PREFERENCE_RESULTS_ITEM.link)
      : location.pathname.startsWith(item.link)

  return (
    <nav id={id} className={`${styles.sidebar} ${className ?? ''}`} aria-label="Main navigation" hidden={hidden}>
      <div className={styles.section}>
        <div className={styles.header}>Admin Portal</div>
        <div className={styles.items}>
          {visibleConsoleItems.map((item, index) => (
            <SidebarItem
              key={index}
              title={item.title}
              link={item.link}
              icon={item.icon}
              isActive={location.pathname.startsWith(item.link)}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      </div>
      {visibleInternalItems.length > 0 && (
        <div className={styles.section}>
          <Tooltip text="Wispr team only" placement="right" hideArrow delay={200}>
            <div className={styles.header}>🔒 Internal</div>
          </Tooltip>
          <div className={styles.items}>
            {visibleInternalItems.map((item, index) => (
              <SidebarItem
                key={index}
                title={item.title}
                link={item.link}
                icon={item.icon}
                isActive={isInternalItemActive(item)}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      )}
      <HelpButton />
    </nav>
  )
}

export default Sidebar
